/**
 * Contrato LPS (paciente) ↔ caja N³ del Vol3D.
 * Las máscaras nativas hay que resamplear a la misma grilla (y flipZ).
 * Un mesh GLTF/GLB del servidor llega en LPS y se transforma a la caja.
 */
import { applyFlipZToPatientZ } from './mpr3dLaterality'

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n))
}

/**
 * @param {{ originLps?: number[], spacing?: number[], flipZ?: boolean, cols?: number, rows?: number, zDim?: number, n?: number, boxScale?: {x,y,z} }} vol
 */
export function lpsToBox(lps, vol) {
  if (!lps || !vol) return { x: 0.5, y: 0.5, z: 0.5 }
  const origin = vol.originLps || [0, 0, 0]
  const sp = vol.spacing || [1, 1, 1]
  const cols = Math.max(1, Number(vol.cols) || 1)
  const rows = Math.max(1, Number(vol.rows) || 1)
  const zDim = Math.max(1, Number(vol.zDim) || 1)
  const ix = (Number(lps[0]) - Number(origin[0] || 0)) / Math.max(1e-9, Number(sp[0]) || 1)
  const iy = (Number(lps[1]) - Number(origin[1] || 0)) / Math.max(1e-9, Number(sp[1]) || 1)
  let iz = (Number(lps[2]) - Number(origin[2] || 0)) / Math.max(1e-9, Number(sp[2]) || 1)
  iz = applyFlipZToPatientZ(iz, zDim, true)
  const n = Math.max(2, Number(vol.n) || Number(vol.size) || 2)
  const n1 = n - 1
  const gx = clamp(ix / Math.max(1, cols - 1) * n1, 0, n1)
  const gy = clamp(iy / Math.max(1, rows - 1) * n1, 0, n1)
  const gz = clamp(iz / Math.max(1, zDim - 1) * n1, 0, n1)
  const box = vol.boxScale || { x: 1, y: 1, z: 1 }
  return {
    x: (gx / n1 - 0.5) * (box.x > 0.05 ? box.x : 1) + 0.5,
    y: (gy / n1 - 0.5) * (box.y > 0.05 ? box.y : 1) + 0.5,
    z: (gz / n1 - 0.5) * (box.z > 0.05 ? box.z : 1) + 0.5,
    gx,
    gy,
    gz,
  }
}

export function boxToLps(boxPt, vol) {
  if (!boxPt || !vol) return [0, 0, 0]
  const n = Math.max(2, Number(vol.n) || Number(vol.size) || 2)
  const n1 = n - 1
  const box = vol.boxScale || { x: 1, y: 1, z: 1 }
  const bx = box.x > 0.05 ? box.x : 1
  const by = box.y > 0.05 ? box.y : 1
  const bz = box.z > 0.05 ? box.z : 1
  const gx = ((Number(boxPt.x) - 0.5) / bx + 0.5) * n1
  const gy = ((Number(boxPt.y) - 0.5) / by + 0.5) * n1
  const gz = ((Number(boxPt.z) - 0.5) / bz + 0.5) * n1
  const cols = Math.max(1, Number(vol.cols) || 1)
  const rows = Math.max(1, Number(vol.rows) || 1)
  const zDim = Math.max(1, Number(vol.zDim) || 1)
  const ix = gx / n1 * Math.max(1, cols - 1)
  const iy = gy / n1 * Math.max(1, rows - 1)
  let iz = gz / n1 * Math.max(1, zDim - 1)
  iz = applyFlipZToPatientZ(iz, zDim, true)
  const origin = vol.originLps || [0, 0, 0]
  const sp = vol.spacing || [1, 1, 1]
  return [
    Number(origin[0] || 0) + ix * (Number(sp[0]) || 1),
    Number(origin[1] || 0) + iy * (Number(sp[1]) || 1),
    Number(origin[2] || 0) + iz * (Number(sp[2]) || 1),
  ]
}

/**
 * Matriz 4×4 (column-major, estilo Three/GLTF) LPS → caja centrada 0.5.
 */
export function lpsToBoxMatrix(vol) {
  const a = lpsToBox([0, 0, 0], vol)
  const b = lpsToBox([1, 0, 0], vol)
  const c = lpsToBox([0, 1, 0], vol)
  const d = lpsToBox([0, 0, 1], vol)
  const m = new Float32Array(16)
  m[0] = b.x - a.x
  m[1] = b.y - a.y
  m[2] = b.z - a.z
  m[3] = 0
  m[4] = c.x - a.x
  m[5] = c.y - a.y
  m[6] = c.z - a.z
  m[7] = 0
  m[8] = d.x - a.x
  m[9] = d.y - a.y
  m[10] = d.z - a.z
  m[11] = 0
  m[12] = a.x
  m[13] = a.y
  m[14] = a.z
  m[15] = 1
  return m
}

/**
 * Resamplea labelmap Uint8 nativo (sx×sy×sz) a N³ (nearest).
 */
export function resampleMaskToGrid(src, srcSize, n, flipZ) {
  const sx = Math.max(1, srcSize.cols | 0)
  const sy = Math.max(1, srcSize.rows | 0)
  const sz = Math.max(1, srcSize.zDim | 0)
  const N = Math.max(8, Math.min(256, n | 0))
  const out = new Uint8Array(N * N * N)
  if (!src || !src.length) return out
  const n1 = Math.max(1, N - 1)
  const x1 = Math.max(1, sx - 1)
  const y1 = Math.max(1, sy - 1)
  const z1 = Math.max(1, sz - 1)
  for (let zi = 0; zi < N; zi++) {
    let zs = Math.round((zi / n1) * z1)
    zs = z1 - zs
    zs = clamp(zs, 0, sz - 1)
    for (let yi = 0; yi < N; yi++) {
      const ys = clamp(Math.round((yi / n1) * y1), 0, sy - 1)
      const dest = zi * N * N + yi * N
      for (let xi = 0; xi < N; xi++) {
        const xs = clamp(Math.round((xi / n1) * x1), 0, sx - 1)
        out[dest + xi] = src[xs + ys * sx + zs * sx * sy] || 0
      }
    }
  }
  return out
}

export function maskBBox(mask, n) {
  const N = n | 0
  if (!mask || !N) return null
  let minX = N
  let minY = N
  let minZ = N
  let maxX = -1
  let maxY = -1
  let maxZ = -1
  let sx = 0
  let sy = 0
  let sz = 0
  let c = 0
  for (let z = 0; z < N; z++) {
    for (let y = 0; y < N; y++) {
      const row = z * N * N + y * N
      for (let x = 0; x < N; x++) {
        if (!mask[row + x]) continue
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (z < minZ) minZ = z
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
        if (z > maxZ) maxZ = z
        sx += x
        sy += y
        sz += z
        c++
      }
    }
  }
  if (c < 1) return null
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
    centroid: { x: sx / c, y: sy / c, z: sz / c },
    count: c,
  }
}

export function isolateLabel(mask, id) {
  if (!mask) return mask
  const out = new Uint8Array(mask.length)
  const v = id | 0
  if (!v) return out
  for (let i = 0; i < mask.length; i++) {
    out[i] = mask[i] === v ? v : 0
  }
  return out
}
